import React, { useState, useEffect, useMemo } from 'react';
import {
  CreditCard,
  DollarSign,
  Download,
  Clock,
  Phone,
  TrendingUp,
  TrendingDown,
  Calendar,
  AlertCircle,
  CheckCircle,
  Crown,
  Zap,
  Users,
  Building,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const PLAN_OVERRIDES = {
  core: {
    name: 'Core Package',
    price: 149,
    period: '/month',
    description: 'Simple, reliable coverage for small businesses.',
    features: [
      '100 calls/month (30s max each)',
      'AI answers on the first ring',
      "Call forwarding to the owner's phone",
      'Automatic message + email summaries',
      'Answers hours, directions, and common FAQs'
    ],
    limits: {
      minutes: null,
      numbers: 1,
      concurrent: 1,
      monthlyCallLimit: 100,
      maxCallDuration: 30
    },
    popular: false,
    tagLine: 'Simple, reliable coverage for small businesses.'
  },
  premium: {
    name: 'Premium Package',
    price: 449,
    period: '/month',
    description: 'For growing teams that need automation and smarter call handling.',
    features: [
      '600 calls/month (45s max each)',
      'Includes every Core feature',
      'Multi-route calls (sales, support, billing, etc.)',
      'Capture orders or appointment details',
      'Structured data capture (name, number, intent)',
      'Searchable call history with email summaries'
    ],
    limits: {
      minutes: null,
      numbers: 3,
      concurrent: 3,
      monthlyCallLimit: 600,
      maxCallDuration: 45
    },
    popular: true,
    tagLine: 'Automation, routing, and insights for growing businesses.'
  },
  enterprise: {
    name: 'Enterprise Package',
    price: 899,
    period: '/month',
    description: 'Built for call centers and multi-location operations.',
    features: [
      '1,500 calls/month (45s max each)',
      'All Premium capabilities included',
      'Enterprise call routing across teams/locations',
      'Advanced FAQ + knowledge base integration',
      'Full transcription archive for QA and compliance',
      'Priority forwarding and escalation workflows'
    ],
    limits: {
      minutes: null,
      numbers: 5,
      concurrent: 10,
      monthlyCallLimit: 1500,
      maxCallDuration: 45
    },
    popular: false,
    tagLine: 'High-capacity routing and oversight for enterprise teams.'
  }
};

const PLAN_ORDER = ['core', 'premium', 'enterprise'];

const DEFAULT_PLAN_OPTIONS = PLAN_ORDER.map((id) => ({
  id,
  ...PLAN_OVERRIDES[id]
}));

const PLAN_ADDONS = [
  {
    name: 'Outbound Call Automation',
    price: 199,
    description: 'Type a message and AI places the outbound call for follow-ups, confirmations, and reminders.'
  },
  {
    name: 'CRM Integration',
    price: 149,
    description: 'Sync call details and outcomes directly into HubSpot, Salesforce, or Zoho.'
  },
  {
    name: 'Analytics Dashboard',
    price: 249,
    description: 'Track call volume, average duration, routing breakdowns, and sentiment trends at a glance.'
  }
];

const PLAN_ID_ALIAS = {
  global: 'enterprise'
};

const resolvePlanId = (planId) => {
  if (!planId) {return planId;}

  const normalized = typeof planId === 'string' ? planId.toLowerCase() : planId;
  return PLAN_ID_ALIAS[normalized] || normalized;
};

const pickDefined = (overrideValue, ...alternatives) => {
  if (overrideValue !== undefined) {
    return overrideValue;
  }

  for (const value of alternatives) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
};

const normalizePlan = (plan, currentPlanId) => {
  if (!plan) {return null;}

  const rawId = typeof plan.id === 'string' ? plan.id.toLowerCase() : plan.id;
  const normalizedId = PLAN_ID_ALIAS[rawId] || rawId;
  const overrides = PLAN_OVERRIDES[normalizedId] || {};

  const priceSource = typeof plan.price === 'number'
    ? plan.price
    : plan.price?.monthly;

  const price = priceSource != null ? Number(priceSource) : pickDefined(overrides.price, 0);

  const period = plan.period || overrides.period || '/month';
  const limitations = plan.limitations || {};
  const planLimits = plan.limits || {};

  const limits = {
    minutes: pickDefined(overrides.limits?.minutes, limitations.monthlyMinutes, limitations.minutes, planLimits.minutes),
    numbers: pickDefined(overrides.limits?.numbers, limitations.phoneNumbers, planLimits.numbers, 0),
    concurrent: pickDefined(overrides.limits?.concurrent, limitations.concurrentCalls, planLimits.concurrent, 0),
    monthlyCallLimit: pickDefined(overrides.limits?.monthlyCallLimit, limitations.monthlyCallLimit, planLimits.monthlyCallLimit, null),
    maxCallDuration: pickDefined(overrides.limits?.maxCallDuration, limitations.maxCallDuration, planLimits.maxCallDuration, null)
  };

  return {
    id: normalizedId,
    name: overrides.name || plan.name,
    price,
    period,
    description: overrides.description || plan.description,
    features: overrides.features || plan.features || [],
    limits,
    popular: pickDefined(overrides.popular, plan.popular, false),
    tagLine: overrides.tagLine || plan.tagLine || '',
    current: normalizedId === currentPlanId
  };
};

const BillingPage = () => {
  const { user } = useAuth();
  const [billingData, setBillingData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState(DEFAULT_PLAN_OPTIONS);
  const [currentPlanId, setCurrentPlanId] = useState(resolvePlanId(user?.tenant?.plan) || DEFAULT_PLAN_OPTIONS[0].id);
  const [loading, setLoading] = useState(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);

  useEffect(() => {
    loadBillingData();
  }, []);

  useEffect(() => {
    if (user?.tenant?.plan) {
      setCurrentPlanId(resolvePlanId(user.tenant.plan));
    }
  }, [user?.tenant?.plan]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [billingRes, usageRes, invoicesRes, plansRes] = await Promise.all([
        billingAPI.getCurrentUsage(),
        billingAPI.getCostAnalysis(),
        billingAPI.getInvoices(),
        billingAPI.getPlans().catch((error) => {
          console.error('Failed to load plan catalog:', error);
          return null;
        })
      ]);

      setBillingData(billingRes.data.data || billingRes.data);
      setUsage(usageRes.data.data || usageRes.data);
      setInvoices(invoicesRes.data.data || invoicesRes.data || []);

      if (plansRes) {
        const planPayload = plansRes.data?.data || plansRes.data;
        const catalog = planPayload?.plans?.length
          ? planPayload.plans
            .map(plan => normalizePlan(plan, resolvePlanId(planPayload.currentPlan)))
            .filter(Boolean)
            .sort((a, b) => {
              const aIndex = PLAN_ORDER.indexOf(a.id);
              const bIndex = PLAN_ORDER.indexOf(b.id);
              const safeAIndex = aIndex === -1 ? PLAN_ORDER.length : aIndex;
              const safeBIndex = bIndex === -1 ? PLAN_ORDER.length : bIndex;
              return safeAIndex - safeBIndex;
            })
          : null;

        if (catalog) {
          setPlans(catalog);
        }

        if (planPayload?.currentPlan) {
          setCurrentPlanId(resolvePlanId(planPayload.currentPlan));
        }
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setCheckoutPlanId(planId);

      const response = await billingAPI.createCheckoutSession({
        planId,
        billingFrequency: 'monthly',
        successUrl: `${window.location.origin}/dashboard/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/dashboard/billing`
      });

      const payload = response.data?.data || response.data;

      if (payload?.url) {
        toast.success('Redirecting you to Stripe Checkout...');
        window.location.href = payload.url;
        return;
      }

      throw new Error('Stripe checkout session did not return a URL');
    } catch (error) {
      console.error('Failed to start checkout session:', error);
      const message = error.response?.data?.error || error.message || 'Failed to start checkout session';
      toast.error(message);
    } finally {
      setCheckoutPlanId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit) {return 0;}
    return Math.min((used / limit) * 100, 100);
  };

  const resolvedPlans = useMemo(() => {
    const fallbackCurrent = currentPlanId || resolvePlanId(user?.tenant?.plan);

    return plans.map(plan => ({
      ...plan,
      current: plan.id === fallbackCurrent
    }));
  }, [plans, currentPlanId, user?.tenant?.plan]);

  const currentPlan = useMemo(() => {
    const planList = resolvedPlans.length > 0 ? resolvedPlans : DEFAULT_PLAN_OPTIONS;
    const active = planList.find(plan => plan.current);
    return active || planList[0] || DEFAULT_PLAN_OPTIONS[0];
  }, [resolvedPlans]);

  const usageSummary = useMemo(() => {
    const safePlan = currentPlan || DEFAULT_PLAN_OPTIONS[0];
    const hasCallLimit = Boolean(safePlan?.limits?.monthlyCallLimit);
    const usedValue = hasCallLimit ? usage?.totalCalls ?? 0 : usage?.minutesUsed ?? 0;
    const limitValue = hasCallLimit ? safePlan?.limits?.monthlyCallLimit : safePlan?.limits?.minutes;
    const changeSource = hasCallLimit ? usage?.callsChange : usage?.minutesChange;
    const changeValue = Number.isFinite(changeSource) ? changeSource : 0;

    const label = hasCallLimit ? 'Calls Used' : 'Minutes Used';

    const limitLabel = (() => {
      if (!limitValue) {
        return 'Usage limit unavailable';
      }
      if (!hasCallLimit && limitValue === -1) {
        return 'Unlimited';
      }
      return hasCallLimit ? `${limitValue} calls/mo` : `${limitValue} minute limit`;
    })();

    const progress = getUsagePercentage(usedValue, limitValue);

    return {
      hasCallLimit,
      usedValue,
      limitValue,
      changeValue,
      label,
      limitLabel,
      progress
    };
  }, [currentPlan, usage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading billing information..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
          <p className="text-gray-600 mt-1">
            Manage your subscription and view usage analytics
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={loadBillingData}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>

          <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Download Invoice
          </button>
        </div>
      </div>

      {/* Current Plan Overview */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center mb-2">
              <Crown className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Current Plan: {currentPlan.name}</h3>
            </div>
            <p className="text-blue-100 mb-4">{currentPlan.description}</p>
            <div className="flex items-center">
              <span className="text-2xl font-bold">{formatCurrency(currentPlan.price)}</span>
              <span className="text-blue-100 ml-1">{currentPlan.period}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Next billing date</p>
            <p className="font-semibold">{formatDate(billingData?.nextBillingDate || new Date())}</p>
            <button
              onClick={() => setUpgradeModalOpen(true)}
              className="mt-3 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>

      {/* Usage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Minutes Used */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="font-medium text-gray-900">{usageSummary.label}</h3>
            </div>
            <span className="text-2xl font-bold text-gray-900">
              {usageSummary.usedValue}
            </span>
          </div>

          <div className="mb-2">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>This month</span>
              <span>{usageSummary.limitLabel}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={clsx(
                  'h-2 rounded-full transition-all duration-300',
                  usageSummary.progress > 80
                    ? 'bg-red-500'
                    : usageSummary.progress > 60
                      ? 'bg-yellow-500'
                      : 'bg-blue-500'
                )}
                style={{
                  width: `${usageSummary.progress}%`
                }}
              />
            </div>
          </div>

          <div className="flex items-center text-sm">
            {usageSummary.changeValue >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              usageSummary.changeValue >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {Math.abs(usageSummary.changeValue || 0)}% vs last month
            </span>
          </div>
        </div>

        {/* Total Calls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Phone className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="font-medium text-gray-900">Total Calls</h3>
            </div>
            <span className="text-2xl font-bold text-gray-900">
              {usage?.totalCalls || 0}
            </span>
          </div>

          <div className="text-sm text-gray-600 mb-3">
            <div className="flex justify-between">
              <span>Successful:</span>
              <span>{usage?.successfulCalls || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Failed:</span>
              <span>{usage?.failedCalls || 0}</span>
            </div>
          </div>

          <div className="flex items-center text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">
              {((usage?.successfulCalls || 0) / (usage?.totalCalls || 1) * 100).toFixed(1)}% success rate
            </span>
          </div>
        </div>

        {/* Current Charges */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="font-medium text-gray-900">Current Charges</h3>
            </div>
            <span className="text-2xl font-bold text-gray-900">
              {formatCurrency(billingData?.currentCharges || 0)}
            </span>
          </div>

          <div className="text-sm text-gray-600 mb-3">
            <div className="flex justify-between">
              <span>Base plan:</span>
              <span>{formatCurrency(currentPlan.price)}</span>
            </div>
            <div className="flex justify-between">
              <span>Usage charges:</span>
              <span>{formatCurrency(billingData?.usageCharges || 0)}</span>
            </div>
          </div>

          <div className="flex items-center text-sm">
            <Calendar className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-gray-600">
              Billing cycle: {formatDate(billingData?.billingPeriodStart)} - {formatDate(billingData?.billingPeriodEnd)}
            </span>
          </div>
        </div>
      </div>

      {/* Usage Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Usage Breakdown</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Usage by Service */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">By Service</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm text-gray-900">Voice Calls</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{usage?.voiceMinutes || 0} min</div>
                  <div className="text-xs text-gray-500">{formatCurrency(usage?.voiceCost || 0)}</div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Zap className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-900">AI Processing</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{usage?.aiRequests || 0} requests</div>
                  <div className="text-xs text-gray-500">{formatCurrency(usage?.aiCost || 0)}</div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Building className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm text-gray-900">Phone Numbers</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{usage?.phoneNumbers || 0} numbers</div>
                  <div className="text-xs text-gray-500">{formatCurrency(usage?.numberCost || 0)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Peak Usage Times */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Peak Usage Hours</h4>
            <div className="space-y-2">
              {[
                { hour: '9:00 AM', usage: 85, percentage: 85 },
                { hour: '2:00 PM', usage: 72, percentage: 72 },
                { hour: '11:00 AM', usage: 68, percentage: 68 },
                { hour: '4:00 PM', usage: 45, percentage: 45 },
                { hour: '10:00 AM', usage: 32, percentage: 32 }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 w-16">{item.hour}</span>
                  <div className="flex-1 mx-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">{item.usage}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Invoices</h3>
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            View All
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Invoice</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length > 0 ? (
                invoices.slice(0, 5).map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">#{invoice.number}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(invoice.date)}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : invoice.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                      )}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button className="text-blue-600 hover:text-blue-700 text-sm">
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No invoices available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upgrade Modal */}
      {upgradeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Choose Your Plan</h3>
                <button
                  onClick={() => setUpgradeModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {resolvedPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={clsx(
                      'border rounded-lg p-6 relative',
                      plan.current
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300',
                      plan.popular && 'ring-2 ring-blue-500'
                    )}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h4>
                      <div className="flex items-center justify-center mb-2">
                        <span className="text-3xl font-bold text-gray-900">
                          {formatCurrency(plan.price)}
                        </span>
                        <span className="text-gray-500 ml-1">{plan.period}</span>
                      </div>
                      <p className="text-sm text-gray-600">{plan.description}</p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="text-sm text-gray-700">
                          - {feature}
                        </li>
                      ))}
                    </ul>

                    {plan.tagLine && (
                      <p className="text-sm text-gray-500 mb-6 italic">{plan.tagLine}</p>
                    )}

                    <button
                      disabled={plan.current || checkoutPlanId === plan.id}
                      onClick={() => handleUpgrade(plan.id)}
                      className={clsx(
                        'w-full py-2 px-4 rounded-lg font-medium transition-colors',
                        plan.current
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      )}
                    >
                      {plan.current ? 'Current Plan' : checkoutPlanId === plan.id ? 'Redirecting…' : 'Upgrade'}
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <h4 className="text-lg font-semibold text-gray-900">Add-On Menu (Upsells)</h4>
                <p className="text-sm text-gray-600 mt-1">Available with any plan.</p>

                <div className="mt-4 space-y-4">
                  {PLAN_ADDONS.map((addon) => (
                    <div key={addon.name} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-baseline">
                        <h5 className="text-base font-semibold text-gray-900">{addon.name}</h5>
                        <span className="text-base font-medium text-gray-900">+{formatCurrency(addon.price)}/mo</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{addon.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
